(function (root, factory) {
  "use strict";
  const exported = factory();
  if (typeof module === "object" && module.exports) module.exports = exported;
  else root.LouderrSoundPlayback = exported;
})(globalThis, () => {
  "use strict";
  const MAX_AGE = 5000, DEADLINE = 2000, MAX_NODES = 512, CONCURRENCY = 2;
  const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
  const mixed = new RegExp(`^/api/(?:sound-renders|video-remix-renders)/${UUID}/media$`, "i");
  const safeURL = new RegExp(`^/api/(media/${UUID}|(?:sound-renders|video-remix-renders)/${UUID}/media)$`, "i");
  const id = new RegExp(`^${UUID}$`, "i");

  // A revalidation signal carries no media authority. Only a current, ordinary
  // post read can select bytes. Private archive reads have a separate context.
  // Every newly rendered borrowed source starts held.
  function create(hooks) {
    const now = hooks.now || (() => performance.now());
    const later = hooks.setTimeout || setTimeout, cancelTimer = hooks.clearTimeout || clearTimeout;
    const records = new WeakMap(), live = new Map(), groups = new Map(), queue = [];
    const identityKey = () => hooks.identity ? hooks.identity() : hooks.scope();
    let scope = hooks.scope(), identity = identityKey(), active = 0, disposed = false;
    function current(entry) { return !disposed && entry.scope === hooks.scope() && entry.scope === scope; }
    function connected(entry) { return current(entry) && entry.media.isConnected; }
    function visible(entry) { return connected(entry) && (!hooks.visible || hooks.visible(entry.media)); }
    function fresh(group) { return group.checkedAt !== null && now() - group.checkedAt < MAX_AGE; }
    function hold(entry, message, manual = true) {
      entry.allowed = false;
      entry.requireManual ||= manual;
      entry.position = Number.isFinite(entry.media.currentTime) && entry.media.getAttribute("src")
        ? entry.media.currentTime : entry.position;
      entry.notice = message;
      entry.intent += 1;
      hooks.hold(entry.media, message);
      hooks.text?.(entry.media, null);
    }
    function finish(request, ok) {
      if (request.finished) return;
      request.finished = true;
      cancelTimer(request.timer);
      if (request.group.request === request) request.group.request = null;
      request.resolve(ok);
    }
    function fail(request) {
      if (request.finished) return;
      if (request.group.request === request && request.scope === hooks.scope()) {
        request.group.checkedAt = null;
        request.group.retryAt = now() + DEADLINE;
        for (const entry of request.group.nodes) if (connected(entry)) hold(entry, "Sound could not be checked. Tap play to retry.");
      }
      request.controller.abort();
      finish(request, false);
    }
    function retire(group) {
      group.epoch += 1;
      if (group.request) { group.request.controller.abort(); finish(group.request, false); }
      for (const entry of group.nodes) {
        hold(entry, "Open this post again to check its sound.");
        live.delete(entry.media);
      }
      groups.delete(group.key);
    }
    function syncScope() {
      const next = hooks.scope();
      if (next === scope) return;
      const nextIdentity = identityKey();
      for (const group of [...groups.values()]) {
        if (nextIdentity !== identity) { retire(group); continue; }
        group.epoch += 1; group.checkedAt = null; group.scope = next;
        if (group.request) { group.request.controller.abort(); finish(group.request, false); }
        for (const entry of group.nodes) { hold(entry, "Checking sound…"); entry.scope = next; }
      }
      scope = next;
      identity = nextIdentity;
    }
    function apply(request, drop) {
      const group = request.group;
      if (request.finished || group.request !== request || request.epoch !== group.epoch
          || request.scope !== hooks.scope() || now() >= request.expires) return false;
      if (drop?.id !== group.id || !Array.isArray(drop.media)) throw new Error("Invalid post selection");
      const selections = new Map();
      for (const entry of group.nodes) {
        const item = drop.media.find(item => item.media_asset_id === entry.assetId);
        if (item && item.media_url === null && item.media_status === "unavailable") {
          selections.set(entry, null);
          continue;
        }
        if (item && (!safeURL.test(item.media_url || "") || !/^(video|audio)\//.test(item.media_type || "")
            || !/^[0-9a-f]{64}$/i.test(item.media_selection_revision || ""))) throw new Error("Invalid media selection");
        selections.set(entry, item);
      }
      group.checkedAt = now(); group.retryAt = 0;
      for (const [entry, item] of selections) {
        if (!connected(entry)) continue;
        if (!item) { hold(entry, "This media is no longer available."); continue; }
        const changed = entry.url !== item.media_url || entry.revision !== item.media_selection_revision;
        const wasHeld = !entry.allowed;
        if (changed) hold(entry, "Sound changed. Tap play to continue.");
        entry.url = item.media_url; entry.revision = item.media_selection_revision;
        entry.borrowed = mixed.test(entry.url);
        entry.soundId = drop.video_remix?.id || drop.sound?.id || entry.soundId;
        entry.sourceDropId = drop.video_remix?.source_drop_id || drop.sound?.source_drop_id || entry.sourceDropId;
        entry.sourceOwnerId = drop.video_remix?.creator?.id || drop.sound?.creator?.id || entry.sourceOwnerId;
        entry.allowed = true;
        if (changed || wasHeld) {
          if (!entry.requireManual) entry.notice = "";
          hooks.select(entry.media, entry.url, {position: entry.position, manual: entry.requireManual, notice: entry.notice});
        }
        hooks.text?.(entry.media, item);
      }
      hooks.accept?.(drop, group.context);
      return true;
    }
    function drain() {
      while (active < CONCURRENCY && queue.length) {
        const request = queue.shift();
        if (request.finished) continue;
        if (request.scope !== hooks.scope() || now() >= request.expires) { fail(request); continue; }
        active += 1;
        // A provider which ignores AbortSignal keeps its slot until it settles.
        // Timeouts deny playback independently; they never create unbounded work.
        Promise.resolve().then(() => hooks.read(request.group.id, request.controller.signal, request.group.context))
          .then(drop => { if (!request.finished) finish(request, apply(request, drop)); })
          .catch(() => fail(request))
          .finally(() => { active -= 1; drain(); });
      }
    }
    function check(group, force = false) {
      if (disposed || group.scope !== hooks.scope()) return Promise.resolve(false);
      if (group.request) return group.request.promise;
      if (!force && fresh(group)) return Promise.resolve(true);
      const request = {group, epoch: group.epoch, scope, expires: now() + DEADLINE,
        controller: new AbortController(), finished: false};
      request.promise = new Promise(resolve => { request.resolve = resolve; });
      request.timer = later(() => fail(request), DEADLINE);
      group.request = request;
      // Expired queued work is bounded by the live-node cap.
      for (let i = queue.length - 1; i >= 0; i--) if (queue[i].finished) queue.splice(i, 1);
      queue.push(request); drain();
      return request.promise;
    }
    function register(media, drop, item, context = null) {
      if (!mixed.test(item?.media_url || "") || records.has(media)) return;
      syncScope();
      const entry = {media, scope, assetId: item.media_asset_id, url: item.media_url,
        revision: item.media_selection_revision, soundId: drop.video_remix?.id || drop.sound?.id || null,
        sourceDropId: drop.video_remix?.source_drop_id || drop.sound?.source_drop_id || null,
        sourceOwnerId: drop.video_remix?.creator?.id || drop.sound?.creator?.id || null,
        authorId: drop.author_id || null,
        borrowed: true, allowed: false, requireManual: false, position: 0, intent: 0, notice: ""};
      records.set(media, entry);
      hold(entry, "Checking sound…", false);
      if (disposed || live.size >= MAX_NODES || !id.test(drop.id || "") || !id.test(entry.assetId || "")) {
        hold(entry, "Open this post again to check its sound."); return;
      }
      const key = JSON.stringify([drop.id, context]);
      let group = groups.get(key);
      if (!group) {
        group = {id: drop.id, key, context, scope, epoch: 0, nodes: new Set(), checkedAt: null, retryAt: 0, request: null};
        groups.set(key, group);
      }
      entry.group = group; group.nodes.add(entry); live.set(media, entry);
      media.addEventListener("pause", () => { entry.intent += 1; });
      media.addEventListener("play", () => {
        if (!canLoad(media)) {
          hold(entry, "Checking sound…");
          if (connected(entry)) void check(group, true);
        }
      });
      media.addEventListener("playing", () => {
        if (media.paused || !canLoad(media)) return;
        entry.notice = "";
        hooks.notice?.(media, "");
      });
      // Hidden/off-screen cards remain held until needed. Rendering finishes
      // synchronously before checking geometry, so long feeds do not flood reads.
      Promise.resolve().then(() => { if (visible(entry)) void check(group, true); });
    }
    function canLoad(media) {
      const entry = records.get(media);
      return !entry || current(entry) && entry.allowed && (!entry.borrowed || fresh(entry.group));
    }
    function canAutoplay(media) {
      const entry = records.get(media);
      return !entry || current(entry) && !entry.requireManual;
    }
    async function beforePlay(media, manual = false) {
      const entry = records.get(media);
      if (!entry) return true;
      if (!connected(entry) || !entry.group || entry.requireManual && !manual) return false;
      const intent = ++entry.intent;
      if (!canLoad(media) && !await check(entry.group, true)) return false;
      if (intent !== entry.intent || !connected(entry) || !canLoad(media)) return false;
      if (manual) { entry.requireManual = false; entry.notice = ""; }
      return !entry.requireManual;
    }
    function invalidate(soundId = null, dropId = null, ownerId = null) {
      syncScope();
      for (const group of groups.values()) {
        if (dropId && group.id !== dropId && ![...group.nodes].some(entry => entry.sourceDropId === dropId)) continue;
        if (soundId && ![...group.nodes].some(entry => entry.soundId === soundId)) continue;
        if (ownerId && ![...group.nodes].some(entry => entry.sourceOwnerId === ownerId || entry.authorId === ownerId)) continue;
        group.epoch += 1; group.checkedAt = null;
        if (group.request) { group.request.controller.abort(); finish(group.request, false); }
        if ([...group.nodes].some(visible)) void check(group, true);
      }
    }
    function tick() {
      syncScope();
      for (const group of [...groups.values()]) {
        for (const entry of group.nodes) if (!entry.media.isConnected) { live.delete(entry.media); group.nodes.delete(entry); }
        if (!group.nodes.size) { retire(group); continue; }
        const playing = [...group.nodes].some(entry => entry.borrowed && !entry.media.paused);
        const visibleBorrowed = [...group.nodes].some(entry => entry.borrowed && visible(entry));
        const held = [...group.nodes].some(entry => !entry.allowed && visible(entry));
        if (group.checkedAt !== null && !fresh(group)) {
          for (const entry of group.nodes) if (entry.borrowed && (!entry.media.paused || visible(entry))) hold(entry, "Checking sound…");
        }
        if (!group.request && now() >= group.retryAt && (held || (playing || visibleBorrowed) && now() - group.checkedAt >= MAX_AGE - 1000)) void check(group, true);
      }
    }
    function revalidateDrop(dropId) {
      syncScope();
      return Promise.all([...groups.values()]
        .filter(group => group.id === dropId && [...group.nodes].some(visible))
        .map(group => check(group)));
    }
    function dispose() {
      for (const group of [...groups.values()]) retire(group);
      disposed = true; queue.length = 0;
    }
    function cancelPlay(media) { const entry = records.get(media); if (entry) entry.intent += 1; }
    function suspend() {
      for (const group of groups.values()) {
        group.epoch += 1; group.checkedAt = null;
        if (group.request) { group.request.controller.abort(); finish(group.request, false); }
        for (const entry of group.nodes) hold(entry, "Checking sound…");
      }
    }
    return {register, beforePlay, cancelPlay, canLoad, canAutoplay, invalidate, revalidateDrop, tick, syncScope, suspend, dispose,
      source: media => records.get(media)?.url, notice: media => records.get(media)?.notice || ""};
  }
  return {create, MAX_AGE, DEADLINE, MAX_NODES, CONCURRENCY};
});
